import { useEffect, useRef, useState } from 'react';

import { buildTriageCreatePayload, citiesForState, OTHER_CITY_OPTION, type GeoState } from './triageHelpers';
import { DedupMatch, ADD_LEAD_SOURCES, AddLeadSource, SearchCombo } from './triageDeskShared';
export function AddLeadModal({
  productOptions,
  onClose,
  onCreated,
  onOpenExisting,
}: {
  productOptions: string[];
  onClose: () => void;
  onCreated: (name: string) => void;
  onOpenExisting: (m: DedupMatch, phone: string) => void;
}) {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  // L137 — State→City cascading pickers (owned gazetteer via GET /geo/india).
  // `otherCity` is the "Other / not listed" free-text escape; `legacyLocation`
  // is the graceful free-text fallback when the geo dataset can't load.
  const [geoStates, setGeoStates] = useState<GeoState[] | null>(null);
  const [geoFailed, setGeoFailed] = useState(false);
  const [stateVal, setStateVal] = useState('');
  const [city, setCity] = useState('');
  const [otherCity, setOtherCity] = useState('');
  const [legacyLocation, setLegacyLocation] = useState('');
  const [productInterest, setProductInterest] = useState('');
  const [source, setSource] = useState<AddLeadSource>('phone-in');
  const [checking, setChecking] = useState(false);
  const [match, setMatch] = useState<DedupMatch | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const dedupSeq = useRef(0);

  // Load the states→cities dataset once per modal open (authed, cached
  // server-side). Failure degrades to the legacy free-text city input.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/v1/geo/india', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((j) => {
        if (cancelled) return;
        const states = j?.data?.states;
        if (Array.isArray(states) && states.length > 0) setGeoStates(states);
        else setGeoFailed(true);
      })
      .catch(() => { if (!cancelled) setGeoFailed(true); });
    return () => { cancelled = true; };
  }, []);

  // LIVE dedup — debounced; fires once the number has 10+ digits.
  useEffect(() => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      setMatch(null);
      setChecking(false);
      return;
    }
    const seq = ++dedupSeq.current;
    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch('/api/v1/lead-capture/customers/dedup-check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone }),
          credentials: 'include',
        });
        const j = await res.json().catch(() => null);
        if (seq !== dedupSeq.current) return; // stale response
        if (res.ok && j?.data?.exists) {
          setMatch({
            existingLeadId: j.data.existingLeadId,
            name: j.data.name ?? null,
            assignedToName: j.data.assignedToName ?? null,
            sourceName: j.data.sourceName ?? null,
            location: j.data.location ?? null,
          });
        } else {
          setMatch(null);
        }
      } catch {
        if (seq === dedupSeq.current) setMatch(null); // fail-open: server 409s anyway
      } finally {
        if (seq === dedupSeq.current) setChecking(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [phone]);

  async function submit() {
    if (submitting || match) return;
    setFormError(null);
    if (phone.replace(/\D/g, '').length < 10) { setFormError('Enter a valid 10-digit mobile number.'); return; }
    if (!name.trim()) { setFormError('Name is required.'); return; }
    setSubmitting(true);
    try {
      // L137 — payload carries the picker state additively; city stays in the
      // existing `location` format ("Other / not listed" routes the free text;
      // geo-load failure degrades to the legacy free-text location).
      const payload = geoFailed
        ? buildTriageCreatePayload({
            phone, name, state: '', city: legacyLocation, otherCity: '',
            productInterest, source,
          })
        : buildTriageCreatePayload({
            phone, name, state: stateVal, city, otherCity, productInterest, source,
          });
      const res = await fetch('/api/v1/lead-capture/triage-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      const j = await res.json().catch(() => null);
      if (res.status === 409 && j?.code === 'DUPLICATE_LEAD') {
        // Server is the source of truth — surface the match and STOP.
        setMatch({
          existingLeadId: j.existingLeadId,
          name: j.name ?? null,
          assignedToName: j.assignedToName ?? null,
          sourceName: j.sourceName ?? null,
          location: null,
        });
        return;
      }
      if (!res.ok) throw new Error(j?.message || j?.error || `HTTP ${res.status}`);
      onCreated(name.trim());
    } catch (e) {
      setFormError(`Failed to add lead: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4"
      onClick={() => !submitting && onClose()}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">New lead</h2>
          <button onClick={onClose} disabled={submitting}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none disabled:opacity-50" aria-label="Close">×</button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Phone-in / walk-in / reference lead. It lands in the Unassigned queue for verification.
        </p>

        <div className="flex flex-col gap-3">
          <label className="text-sm">
            <span className="block text-xs font-medium text-gray-600 mb-1">Mobile number *</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 98123 45678"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            {checking && <span className="text-xs text-gray-400">Checking for an existing lead…</span>}
          </label>

          {match && (
            <div className="p-3 rounded border border-amber-300 bg-amber-50">
              <div className="text-sm font-medium text-amber-900">This number already has a lead</div>
              <div className="text-sm text-gray-800 mt-1">
                {match.name || '(no name)'}
                {match.location ? <span className="text-gray-500"> · {match.location}</span> : null}
              </div>
              <div className="text-xs text-gray-600 mt-0.5">
                {match.sourceName ? `Source: ${match.sourceName}` : ''}
                {match.assignedToName ? ` · Assigned to ${match.assignedToName}` : ' · Unassigned'}
              </div>
              <button
                onClick={() => onOpenExisting(match, phone)}
                className="mt-2 text-xs px-3 py-1.5 rounded bg-amber-600 text-white font-medium hover:bg-amber-700"
              >
                Open existing lead
              </button>
            </div>
          )}

          <label className="text-sm">
            <span className="block text-xs font-medium text-gray-600 mb-1">Customer name *</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>

          {/* L137 — State first, then City (cascading, searchable, complete
              India data from the owned gazetteer). "Other / not listed"
              opens a free-text row so unlisted villages never block. */}
          {geoFailed ? (
            <label className="text-sm">
              <span className="block text-xs font-medium text-gray-600 mb-1">City / location</span>
              <input
                type="text"
                value={legacyLocation}
                onChange={(e) => setLegacyLocation(e.target.value)}
                placeholder="e.g. Panipat"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
          ) : (
            <>
              <SearchCombo
                label="State"
                value={stateVal}
                options={(geoStates ?? []).map((s) => s.name)}
                placeholder={geoStates ? 'Search state…' : 'Loading states…'}
                disabled={!geoStates}
                onSelect={(s) => {
                  setStateVal(s);
                  // Cascade: a state change resets the city pick.
                  setCity('');
                  setOtherCity('');
                }}
              />
              <SearchCombo
                label="City"
                value={city}
                options={[...citiesForState(geoStates ?? [], stateVal), OTHER_CITY_OPTION]}
                placeholder={stateVal ? 'Search city…' : 'Pick a state first'}
                disabled={!stateVal}
                onSelect={(c) => setCity(c)}
              />
              {city === OTHER_CITY_OPTION && (
                <label className="text-sm">
                  <span className="block text-xs font-medium text-gray-600 mb-1">City / village (free text)</span>
                  <input
                    type="text"
                    value={otherCity}
                    onChange={(e) => setOtherCity(e.target.value)}
                    placeholder="Type the city / village name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </label>
              )}
            </>
          )}

          <label className="text-sm">
            <span className="block text-xs font-medium text-gray-600 mb-1">Product interest</span>
            {productOptions.length > 0 ? (
              <select
                value={productInterest}
                onChange={(e) => setProductInterest(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white"
              >
                <option value="">— Select —</option>
                {productOptions.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={productInterest}
                onChange={(e) => setProductInterest(e.target.value)}
                placeholder="e.g. SS Tank"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
          </label>

          <div className="text-sm">
            <span className="block text-xs font-medium text-gray-600 mb-1">Source *</span>
            <div className="flex items-center gap-1">
              {ADD_LEAD_SOURCES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSource(s.key)}
                  className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                    source === s.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {formError && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">{formError}</div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              disabled={submitting}
              className="text-sm px-3 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || checking || !!match}
              title={match ? 'This number already has a lead — open it instead' : undefined}
              className="text-sm px-4 py-2 rounded bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Adding…' : 'Add lead'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
